import type { PricingModel } from '@/features/pricing/types'

// Model names exposed by the gateway are capability-agnostic. Keep media,
// audio, embedding, and moderation models out of text-only setting pickers.
const NON_TEXT_MODEL_HINTS = [
  'image',
  'flux',
  'sora',
  'veo',
  'kling',
  'jimeng',
  'vidu',
  'midjourney',
  'dall',
  'imagen',
  'banana',
  'recraft',
  'ideogram',
  'stable-diffusion',
  'sdxl',
  'video',
  'seedance',
  'seedream',
  'hailuo',
  'runway',
  'pika',
  'omni_flash',
  'tts',
  'whisper',
  'audio',
  'speech',
  'embedding',
  'embed',
  'bge',
  'rerank',
  'moderation',
]

export function isTextModel(name: string): boolean {
  const normalized = name.toLowerCase()
  if (
    /^(?:omni(?:[-_ ]|$)|wan(?:[-_ ]?\d|$)|minimax[-_ ]h3)/.test(normalized)
  ) {
    return false
  }
  if (/(?:^|[-_ ])(?:i2v|r2v|v2v|t2v)(?:[-_ ]|$)/.test(normalized)) {
    return false
  }
  return !NON_TEXT_MODEL_HINTS.some((hint) => normalized.includes(hint))
}

type TextModelMetadata = Pick<
  PricingModel,
  | 'model_name'
  | 'supported_endpoint_types'
  | 'billing_usage_schema'
  | 'billing_plugin_variants'
>

// Protocol wrappers may expose video through chat/responses, so usage facts
// take precedence over the endpoint name. Unknown names retain the fallback.
export function filterTextModelOptions(
  names: string[],
  metadata: TextModelMetadata[] = []
): string[] {
  const byName = new Map(
    metadata.map((model) => [model.model_name.toLowerCase(), model])
  )
  const textEndpoints = new Set([
    'openai',
    'openai-response',
    'openai-response-compact',
    'anthropic',
    'gemini',
  ])
  return [...new Set(names)]
    .filter((name) => {
      if (!isTextModel(name)) return false
      const model = byName.get(name.toLowerCase())
      if (!model) return true
      const schemas = [
        model.billing_usage_schema,
        ...(model.billing_plugin_variants ?? []).map(
          (variant) => variant.billing_usage_schema
        ),
      ]
      if (
        schemas.some(
          (schema) => schema && ('seconds' in schema || 'image_count' in schema)
        )
      )
        return false
      const endpoints = model.supported_endpoint_types ?? []
      return (
        endpoints.length === 0 ||
        endpoints.some((endpoint) => textEndpoints.has(endpoint))
      )
    })
    .sort()
}
